import {Spin, Form, Input} from 'antd';
import {FormInstance, useForm} from 'antd/es/form/Form';
import React, {Key, useEffect, useState, useCallback, useMemo} from 'react';

import {useCustomTable} from 'components/@common/widgets/custom-table/custom-table';
import {SimpleActionButton} from 'components/form/components';
import {CustomSelect} from 'components/form/inputs/custom-select';
import {ModalOpenerComponent} from 'components/modal-opener-component';
import {DateCell} from 'components/table/columns/date-cell/date-cell';
import {selectContextRawData} from 'modules/context/context-selector';
import {selectReferenceEntityData} from 'modules/data/data-selectors';
import {usePagination} from 'shared/hooks/use-pagination';
import {showMessageFromResponse} from 'shared/utils';
import {useAppSelector} from 'store/config/hooks';

import {IPostISubmitGroupCompany, ISubmitGroupCompanyDto, ISendCroupCompanyService} from '../../../api/types';
import {useSubmitGroupCompanyColumns} from '../../../hooks/use-submit-group-company-columns';
import {AIS_SERVICES_LOOKUP_URL, CHANGE_GROUP_COMPANY_V5_SERVICE_NAME} from '../../../model/constants';
import {SubmitGroupCompanyTabService} from '../../../services';
import {ActionPanel} from '../../action-panel';
import {SubmitGroupCompanyForm} from '../../forms/submit-group-company-form';

export const SubmitGroupCompanyTab = () => {
    const [tableData, setTableData] = useState<ISubmitGroupCompanyDto[]>();

    const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);

    const [isLoadingTableData, setIsLoadingTableData] = useState<boolean>(false);

    const [totalRows, setTotalRows] = useState<number>(0);

    const [currentPage, setCurrentPage] = useState<number>(1);

    const {pageSize, handleChange} = usePagination({pageDefault: 1, pageSizeDefault: 10});

    const aisServicesLookups = useAppSelector(selectReferenceEntityData(AIS_SERVICES_LOOKUP_URL));

    const [form] = useForm();
    const {organizationId} = useAppSelector(selectContextRawData) ?? {};

    useEffect(() => {
        if (!aisServicesLookups?.length) return;

        const changeCompanyGroupInfoServiceV5 = aisServicesLookups.find(
            (lookup) => lookup.lookupCode === CHANGE_GROUP_COMPANY_V5_SERVICE_NAME
        );

        if (changeCompanyGroupInfoServiceV5?.id) {
            form?.setFieldsValue({externalServiceId: changeCompanyGroupInfoServiceV5?.id});
        }
    }, [aisServicesLookups]);

    const fetchData = useCallback(async () => {
        try {
            setIsLoadingTableData(true);

            const {response, isError} = await SubmitGroupCompanyTabService.get({
                paginationPageSize: pageSize,
                paginationCurrent: currentPage,
            });

            if (isError) {
                showMessageFromResponse({response, isError});
            }

            const {data} = response;

            setTableData(data.dataTable);
            setTotalRows(data.recordsTotal);
            setIsLoadingTableData(false);
        } catch (e) {
            console.error('Ошибка получения записей: ', e);
        }
    }, [pageSize, currentPage]);

    const columns = useSubmitGroupCompanyColumns(tableData, fetchData);

    useEffect(() => {
        fetchData();
    }, [pageSize, currentPage, organizationId]);

    const {customTableJSX} = useCustomTable({
        rowKey: 'id',
        pagination: {
            position: ['bottomLeft'],
            total: (() => totalRows)(),
            pageSize,
            onChange: (value: number) => {
                handleChange(value);
                setCurrentPage(value);
            },
        },
        rowSelection: {
            selectedRowKeys,
            onChange: (selectedTableRowKeys, selectedRows) => {
                if (selectedTableRowKeys.length === 1) {
                    form.setFieldsValue({date: selectedRows[0]?.reportingDate, code: selectedRows[0]?.groupCode});
                }

                setSelectedRowKeys(selectedTableRowKeys);
            },
        },
        className: 'mt-5',
        dataSource: tableData,
        bordered: true,
        columns,
    });

    const handleDeleteRows = useCallback(async (rowsIds: Key[]) => {
        try {
            const {response, isError} = await SubmitGroupCompanyTabService.delete({rowsIds});

            if (isError) {
                showMessageFromResponse({response, isError});
            }

            setSelectedRowKeys([]);
            await fetchData();
        } catch (e) {
            console.error(e);
        }
    }, []);

    const handleFinish = useCallback(async (values: IPostISubmitGroupCompany) => {
        try {
            const {response, isError} = await SubmitGroupCompanyTabService.post(values);

            if (isError) {
                showMessageFromResponse({response, isError});
            }

            await fetchData();
        } catch (e) {
            console.error(e);
        }
    }, []);

    const handleSendServiceFinish = async (values: ISendCroupCompanyService) => {
        try {
            const {response, isError} = await SubmitGroupCompanyTabService.sendService(values);

            if (isError) {
                showMessageFromResponse({response, isError});
            }

            await fetchData();
        } catch (e) {
            console.error(e);
        }
    };

    const customComponents = useMemo(
        () => (
            <ModalOpenerComponent
                component={
                    <SimpleActionButton
                        disabled={selectedRowKeys?.length !== 1}
                        type="primary"
                        className="button-default"
                        title="Отправить запрос"
                    />
                }
                controlsStyle={{marginTop: '20px'}}
                modalProps={{title: 'Вызвать сервис'}}
                controlLabels={{save: 'Запуск'}}
                handleSave={(setIsModalOpen) => {
                    form.submit();
                    setSelectedRowKeys([]);
                    setIsModalOpen(false);
                }}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSendServiceFinish}
                >
                    <Form.Item
                        name="externalServiceId"
                        label="Наименование сервиса"
                    >
                        <CustomSelect
                            settings={{
                                isDisabled: true,
                                url: AIS_SERVICES_LOOKUP_URL,
                                valuePath: 'id',
                                labelPath: 'description',
                            }}
                        />
                    </Form.Item>
                    <Form.Item
                        name="date"
                        label="Дата, по состоянию на которую представляются сведения"
                    >
                        <DateCell
                            disabled
                            format="DD.MM.YYYY"
                        />
                    </Form.Item>
                    <Form.Item
                        name="code"
                        label="Код группы компаний (холдинга)"
                    >
                        <Input disabled />
                    </Form.Item>
                </Form>
            </ModalOpenerComponent>
        ),
        [selectedRowKeys]
    );

    return (
        <>
            <ActionPanel<IPostISubmitGroupCompany>
                config={{
                    selectedRowKeys,
                    handleDeleteRows,
                    handleFinish,
                    title: 'Запросы на изменение сведений о наименовании группы компаний и (или) головной организации группы компаний',
                }}
                customComponents={customComponents}
            >
                {(formInstance: FormInstance, onFinish?: (values: IPostISubmitGroupCompany) => Promise<void>) => (
                    <SubmitGroupCompanyForm<IPostISubmitGroupCompany>
                        formInstance={formInstance}
                        onFinish={onFinish!}
                    />
                )}
            </ActionPanel>
            <Spin spinning={isLoadingTableData}>{customTableJSX}</Spin>
        </>
    );
};
